import type { BillingTopupDisabledReason } from "@server/types/responseTypes";
import {
  useInfiniteQuery,
  useIsMutating,
  useQuery,
} from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  fetchBillingPurchases,
  fetchBillingSummary,
  fetchTopupOptions,
  isBillingForbidden,
} from "@/api/billing";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { queryKeys } from "@/lib/query-keys";
import { useOrganizationStore } from "@/store/organization";
import { AutoRefillPanel } from "./AutoRefillPanel";
import {
  type BillingPurchaseSnapshot,
  type BillingReturn,
  parseBillingReturn,
  readAttempt,
  requiresPurchaseReview,
} from "./billing-attempt";
import { formatAvailableBalance, formatMinor } from "./billing-format";
import { CreditTopUpDialog } from "./CreditTopUpDialog";
import { InvoiceList } from "./InvoiceList";
import { PurchaseStatus } from "./PurchaseStatus";

const disabledCopy: Record<BillingTopupDisabledReason, string> = {
  account_missing: "Credit billing is not set up for this organization.",
  provisioning: "Credit billing is being set up. Please check again shortly.",
  sales_disabled: "New credit purchases are temporarily unavailable.",
  not_eligible: "This organization is not eligible for credit top-ups.",
  no_offers: "There are no credit packages available right now.",
};

/** Admin-only billing shell; switching unmounts all organization-owned reads and cached content. */
export function BillingPage({
  search,
}: {
  search?: Record<string, unknown>;
} = {}) {
  const organization = useOrganizationStore((state) => state.organization);
  const switching = useOrganizationStore((state) => state.isSwitching);
  // Preserve repeated query values so validation cannot silently choose an identity.
  const raw =
    search ??
    Object.fromEntries(
      [...new URLSearchParams(window.location.search).keys()].map((key) => {
        const values = new URLSearchParams(window.location.search).getAll(key);
        return [key, values.length === 1 ? values[0] : values];
      }),
    );
  const parsed = parseBillingReturn(raw);
  if (switching)
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        Switching organization…
      </div>
    );
  if (!organization)
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        Choose an organization to view billing.
      </div>
    );
  if (organization.role !== "admin")
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        Only organization admins can access billing.
      </div>
    );
  return (
    <OrganizationBilling
      key={`${organization.id}:${parsed.returnTo?.purchaseId ?? ""}`}
      orgId={organization.id}
      returnTo={parsed.returnTo}
      invalidReturn={parsed.invalid}
    />
  );
}

function OrganizationBilling({
  orgId,
  returnTo,
  invalidReturn,
}: {
  orgId: string;
  returnTo?: BillingReturn;
  invalidReturn: boolean;
}) {
  const unavailableId = useId();
  const checkoutPending =
    useIsMutating({ mutationKey: queryKeys.billing.checkout(orgId) }) > 0;
  const [attempt, setAttempt] = useState(() => {
    try {
      return readAttempt(orgId);
    } catch {
      return null;
    }
  });
  const [canResume, setCanResume] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<string | undefined>(
    returnTo?.organizationId === orgId
      ? returnTo.purchaseId
      : attempt?.purchaseId,
  );
  const [switchError, setSwitchError] = useState(false);
  const invoiceSection = useRef<HTMLElement>(null);
  const purchaseReturnFocus = useRef<HTMLElement | null>(null);
  const [purchaseDisplay, setPurchaseDisplay] = useState<
    "inline" | "dialog" | "closed"
  >("inline");
  useEffect(() => {
    if (checkoutPending) return;
    try {
      // A create response may belong to an earlier org view that unmounted before it bound the ID.
      const saved = readAttempt(orgId);
      setAttempt(saved);
      if (saved?.purchaseId && !attempt?.purchaseId) {
        setSelectedPurchase(saved.purchaseId);
        setPurchaseDisplay("inline");
      }
    } catch {
      /* Read-only status remains available when browser storage is unavailable. */
    }
  }, [orgId, checkoutPending, attempt?.purchaseId]);
  const organizations = useOrganizationStore((state) => state.organizations);
  const targetOrg =
    returnTo && returnTo.organizationId !== orgId
      ? organizations.find((org) => org.id === returnTo.organizationId)
      : undefined;
  const foreign = returnTo && returnTo.organizationId !== orgId;
  const summary = useQuery({
    queryKey: queryKeys.billing.summary(orgId),
    queryFn: () => fetchBillingSummary(orgId),
    retry: false,
  });
  const options = useQuery({
    queryKey: queryKeys.billing.options(orgId),
    queryFn: () => fetchTopupOptions(orgId),
    retry: false,
  });
  const history = useInfiniteQuery({
    queryKey: queryKeys.billing.purchases(orgId),
    queryFn: ({ pageParam }) => fetchBillingPurchases(orgId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => (page.has_more ? page.next_cursor : undefined),
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.pages[0]?.purchases.some(
        (p) =>
          p.auto_refill &&
          ((p.payment_status === "not_started" &&
            p.fulfillment_status === "not_started") ||
            p.payment_status === "pending" ||
            p.fulfillment_status === "pending"),
      )
        ? 5000
        : false,
  });
  const onStatus = useCallback(
    (purchase: BillingPurchaseSnapshot) => {
      try {
        const saved = readAttempt(orgId);
        setAttempt(saved);
        // Inspecting history must not change the recovery state of a different attempt.
        if (saved?.purchaseId === purchase.purchase_id) {
          setCanResume(
            !requiresPurchaseReview(purchase) &&
              (purchase.payment_status === "not_started" ||
                purchase.payment_status === "pending") &&
              purchase.fulfillment_status === "not_started",
          );
        }
      } catch {
        /* Read-only status remains available when browser storage is unavailable. */
      }
    },
    [orgId],
  );
  if ([summary.error, options.error, history.error].some(isBillingForbidden))
    return (
      <main className="dashboard-page space-y-3">
        <h1 className="page-title">Billing</h1>
        <p role="alert">Billing access denied</p>
        <p>
          Your current account is not authorized to access this organization's
          billing. Contact an organization admin to restore access.
        </p>
      </main>
    );
  const data = summary.data;
  const canBuy = options.data?.can_top_up === true;
  const disabledReason = options.data?.top_up_disabled_reason;
  const canOpenCheckout =
    !checkoutPending && (attempt ? !attempt.purchaseId || canResume : canBuy);
  const checkoutUnavailable = attempt
    ? "Check your existing purchase before starting another payment."
    : disabledReason
      ? disabledCopy[disabledReason]
      : "Credit packages are currently unavailable.";
  return (
    <main className="dashboard-page billing-page">
      <header className="page-heading">
        <div>
          <h1 className="page-title">Billing</h1>
          <p className="mt-2.5 text-sm text-muted-foreground">
            Manage your API credits and billing history.
          </p>
        </div>
      </header>
      <div className="billing-content">
        {invalidReturn && (
          <p role="alert">
            Invalid Checkout return. Open the purchase from this organization's
            history to check its status.
          </p>
        )}
        {foreign && (
          <section className="border rounded-lg bg-transparent p-4 space-y-2">
            <p>
              This Checkout belongs to another organization. Switch
              organizations to check its status.
            </p>
            {targetOrg ? (
              <Button
                onClick={() => {
                  setSwitchError(false);
                  void useOrganizationStore
                    .getState()
                    .setOrganization(targetOrg)
                    .catch(() => setSwitchError(true));
                }}
              >
                Switch to {targetOrg.name}
              </Button>
            ) : (
              <p>
                Ask an admin of the original organization to check this
                purchase.
              </p>
            )}
            {switchError && (
              <p role="alert">
                Could not switch organization. Please try again.
              </p>
            )}
          </section>
        )}
        <section
          aria-label="Credit summary"
          className="flex min-h-[216px] flex-col justify-between gap-7 rounded-xl bg-card p-8 sm:flex-row sm:items-center"
        >
          <div className="min-w-0 space-y-4">
            {summary.isPending && <p>Loading credit summary…</p>}
            {summary.isError && (
              <p role="alert">Credit summary is temporarily unavailable.</p>
            )}
            {data?.status === "ready" ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Available Balance
                </p>
                <p className="text-[clamp(36px,5vw,56px)] font-medium leading-none tracking-tight break-all">
                  {formatAvailableBalance(data.available_atoms, data.currency)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Remaining credits after charges and reservations.
                </p>
              </>
            ) : (
              data && <p>{disabledCopy[data.status]}</p>
            )}
            {options.isError && (
              <p role="alert">Credit packages are temporarily unavailable.</p>
            )}
            {(summary.isError || options.isError) && (
              <Button
                variant="outline"
                disabled={summary.isFetching || options.isFetching}
                onClick={() => {
                  void summary.refetch();
                  void options.refetch();
                }}
              >
                Try again
              </Button>
            )}
          </div>
          <div className="shrink-0">
            <span title={!canOpenCheckout ? checkoutUnavailable : undefined}>
              <Button
                size="sm"
                className="h-11 gap-2 px-4 text-sm"
                disabled={!canOpenCheckout}
                aria-describedby={!canOpenCheckout ? unavailableId : undefined}
                onClick={() => setDialog(true)}
              >
                {!(attempt && canOpenCheckout) && <Plus className="size-3.5" />}
                {attempt && canOpenCheckout ? "Resume Checkout" : "Add credits"}
              </Button>
              {!canOpenCheckout && (
                <span id={unavailableId} className="sr-only">
                  {checkoutUnavailable}
                </span>
              )}
            </span>
          </div>
        </section>
        {/* Start the independent settings read with balance; known non-prepaid accounts still hide the panel. */}
        {(summary.isPending ||
          (data?.status === "ready" &&
            data.billing_model === "prepaid_credits")) && (
          <AutoRefillPanel key={orgId} orgId={orgId} />
        )}
        {!foreign && selectedPurchase && (
          <PurchaseStatus
            key={selectedPurchase}
            orgId={orgId}
            purchaseId={selectedPurchase}
            onStatus={onStatus}
            dialog={
              purchaseDisplay === "inline"
                ? undefined
                : {
                    open: purchaseDisplay === "dialog",
                    onClose: () => setPurchaseDisplay("closed"),
                    onViewInvoices: () => {
                      purchaseReturnFocus.current = invoiceSection.current;
                      setPurchaseDisplay("closed");
                    },
                    restoreFocus: () => {
                      const target = purchaseReturnFocus.current;
                      if (!target?.isConnected) return;
                      target.focus({ preventScroll: true });
                      target.scrollIntoView({ block: "nearest" });
                    },
                  }
            }
          />
        )}
        <section
          aria-label="Purchase history"
          className="billing-history min-w-0 space-y-5"
        >
          <h2 className="text-[17px] font-semibold">Purchase history</h2>
          {history.isPending && <p>Loading purchases…</p>}
          {history.isError && (
            <>
              <p role="alert">Purchase history is temporarily unavailable.</p>
              <Button
                variant="outline"
                disabled={history.isFetching}
                onClick={() => void history.refetch()}
              >
                Retry history
              </Button>
            </>
          )}
          {!history.isError &&
            history.data?.pages[0].purchases.length === 0 && (
              <p className="text-muted-foreground">No credit purchases yet.</p>
            )}
          {history.data?.pages
            .flatMap((page) => page.purchases)
            .map((purchase) => (
              <div
                key={purchase.purchase_id}
                className="flex flex-wrap items-center justify-between gap-2 border-b py-2"
              >
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2">
                    <span>
                      {formatMinor(purchase.amount_minor, purchase.currency)} ·{" "}
                      {new Date(purchase.created_at).toISOString().slice(0, 10)}
                    </span>
                    {purchase.auto_refill && (
                      <Badge variant="secondary">Auto refill</Badge>
                    )}
                  </p>
                  {purchase.auto_refill &&
                    purchase.credit_amount_minor !== undefined && (
                      <p className="text-sm">
                        Credit amount:{" "}
                        {formatMinor(
                          purchase.credit_amount_minor,
                          purchase.currency,
                        )}
                      </p>
                    )}
                  <p className="text-sm text-muted-foreground">
                    Payment: {purchase.payment_status.replaceAll("_", " ")} ·
                    Credits: {purchase.fulfillment_status.replaceAll("_", " ")}
                  </p>
                  {requiresPurchaseReview(purchase) && (
                    <p className="text-sm">Payment needs review</p>
                  )}
                  {purchase.auto_refill && (
                    <span className="text-xs text-muted-foreground break-all">
                      {purchase.purchase_id}
                    </span>
                  )}
                </div>
                {!purchase.auto_refill && (
                  <Button
                    variant="outline"
                    onClick={(event) => {
                      purchaseReturnFocus.current = event.currentTarget;
                      setSelectedPurchase(purchase.purchase_id);
                      setPurchaseDisplay("dialog");
                    }}
                  >
                    View purchase{" "}
                    <span className="sr-only">{purchase.purchase_id}</span>
                  </Button>
                )}
              </div>
            ))}
          {history.hasNextPage && (
            <Button
              variant="outline"
              disabled={history.isFetching}
              onClick={() => void history.fetchNextPage()}
            >
              Load more purchases
            </Button>
          )}
        </section>
        <section
          ref={invoiceSection}
          tabIndex={-1}
          aria-label="Billing documents"
          className="billing-documents min-w-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <InvoiceList orgId={orgId} />
        </section>
        {dialog && (
          <CreditTopUpDialog
            orgId={orgId}
            options={options.data}
            attempt={attempt}
            onAttempt={(saved, result) => {
              if (result) setCanResume(false);
              setAttempt(readAttempt(orgId));
              if (saved.purchaseId) {
                setSelectedPurchase(saved.purchaseId);
                setPurchaseDisplay("inline");
              }
            }}
            onClose={() => setDialog(false)}
          />
        )}
      </div>
    </main>
  );
}
